// A readable text rendering of the Core IR, for goldfile tests and `--emit-core`.
//
// The point of a goldfile over Core is that a change in *monomorphization* — a
// default specialized differently, a call that flips static/dynamic, a lost type
// — shows up as a reviewable diff, the same way the trace CSVs catch semantics
// changes. So this renders the decisions Core encodes (per-type specialized
// functions, `staticCall` vs `dynCall`, concrete types) in a compact, stable form.

import { Type } from "../frontend/types.js";
import { CoreExpr, CoreFunc, CoreIter, CoreProgram, CoreStmt } from "./core.js";

export function dumpCore(program: CoreProgram): string {
  const out: string[] = [`// memory: ${program.memory}`, ""];
  for (const s of program.structs) {
    const kind = s.valueType ? "value" : "struct";
    const fields = s.fields.map((f) => `${f.name}: ${ty(f.type)}${f.key ? " key" : ""}`).join(", ");
    out.push(`${kind} ${s.name} { ${fields} }`);
  }
  for (const e of program.enums) {
    const variants = e.variants
      .map((v) => (v.fields.length ? `${v.name}(${v.fields.map((f) => `${f.name}: ${ty(f.type)}`).join(", ")})` : v.name))
      .join(" | ");
    out.push(`enum ${e.name} { ${variants} }`);
  }
  if (program.structs.length || program.enums.length) out.push("");
  out.push(...program.funcs.map(fn).flatMap((f) => [...f, ""]));
  for (const d of program.dispatch) {
    const params = [`self: ${d.trait}`, ...d.params.map((p) => ty(p))].join(", ");
    const ret = d.ret.k === "void" ? "" : ` ${ty(d.ret)}`;
    out.push(`dispatch ${d.trait}.${d.method}(${params})${ret} {`);
    for (const c of d.cases) out.push(`  ${ty(c.type)} => ${c.target}`);
    out.push("}", "");
  }
  return out.join("\n").trimEnd() + "\n";
}

function fn(f: CoreFunc): string[] {
  const params = [...(f.self ? [`self: ${ty(f.self.type)}`] : []), ...f.params.map((p) => `${p.name}: ${ty(p.type)}`)];
  const ret = f.ret.k === "void" ? "" : ` ${ty(f.ret)}`;
  return [`fn ${f.id}(${params.join(", ")})${ret} {`, ...f.body.flatMap((s) => stmt(s, 1)), `}`];
}

function stmt(s: CoreStmt, d: number): string[] {
  const pad = "  ".repeat(d);
  switch (s.s) {
    case "let": return [`${pad}let ${s.name}: ${ty(s.type)} = ${expr(s.value)}`];
    case "assign": {
      const lv = s.target.lv === "local" ? s.target.name : `${expr(s.target.object)}.${s.target.field}`;
      return [`${pad}${lv} = ${expr(s.value)}`];
    }
    case "expr": return [`${pad}${expr(s.expr)}`];
    case "return": return [`${pad}return${s.value ? " " + expr(s.value) : ""}`];
    case "if": {
      const lines = [`${pad}if ${expr(s.cond)} {`, ...s.then.flatMap((x) => stmt(x, d + 1))];
      if (s.else) lines.push(`${pad}} else {`, ...s.else.flatMap((x) => stmt(x, d + 1)));
      lines.push(`${pad}}`);
      return lines;
    }
    case "while": return [`${pad}while ${expr(s.cond)} {`, ...s.body.flatMap((x) => stmt(x, d + 1)), `${pad}}`];
    case "for": return [`${pad}for ${s.varName}: ${ty(s.varType)} in ${iter(s.iter)} {`, ...s.body.flatMap((x) => stmt(x, d + 1)), `${pad}}`];
    case "match":
      return [
        `${pad}match ${expr(s.subject)}: ${s.enumName} {`,
        ...s.arms.flatMap((a) => [
          `${pad}  ${a.variant}${a.bindings.length ? `(${a.bindings.map((b) => b.name).join(", ")})` : ""} =>`,
          ...a.body.flatMap((x) => stmt(x, d + 2)),
        ]),
        `${pad}}`,
      ];
    case "go": return [`${pad}go ${s.target}(${s.args.map(expr).join(", ")})`];
    case "txn": {
      const strat = s.strategy.kind === "occ" ? "occ" : `lock: ${s.strategy.footprint.join(", ")}`;
      return [`${pad}txn (${strat}) {`, ...s.body.flatMap((x) => stmt(x, d + 1)), `${pad}}`];
    }
    case "retry": return [`${pad}retry`];
    case "block": return [`${pad}{`, ...s.body.flatMap((x) => stmt(x, d + 1)), `${pad}}`];
  }
}

function iter(it: CoreIter): string {
  switch (it.it) {
    case "collection": return `${expr(it.value)} (${it.container}<${ty(it.elem)}>)`;
    case "iterator": return `${expr(it.value)} (Iterator via ${driver(it.next)})`;
    case "iterable": return `${expr(it.value)} (Iterable via ${driver(it.iter)} -> ${driver(it.next)})`;
  }
}

function driver(d: { via: "static"; target: string } | { via: "dynamic"; trait: string; method: string }): string {
  return d.via === "static" ? d.target : `dyn ${d.trait}.${d.method}`;
}

function expr(x: CoreExpr): string {
  switch (x.e) {
    case "int": return String(x.value);
    case "float": return Number.isInteger(x.value) ? `${x.value}.0` : String(x.value);
    case "string": return JSON.stringify(x.value);
    case "char": return `'${String.fromCodePoint(x.value)}'`;
    case "bool": return String(x.value);
    case "local": return x.name;
    case "field": return `${expr(x.object)}.${x.field}`;
    case "unary": return `${x.op}${expr(x.operand)}`;
    case "binop": return `(${expr(x.left)} ${x.op} ${expr(x.right)})`;
    case "if": return `(if ${expr(x.cond)} { ${expr(x.then)} } else { ${expr(x.else)} })`;
    // A direct call to a concrete monomorphized function — its symbol (`Type_method`
    // for a method, receiver first). `_` marks a resolved static call.
    case "staticCall": return `${x.target}(${x.args.map(expr).join(", ")})`;
    // The runtime-dispatch primitive: prefixed `dyn` (and `.`, not `_`) so it reads
    // as a dynamic dispatch, never mistaken for a concrete `Type_method` symbol.
    case "dynCall": return `dyn ${x.trait}.${x.method}(${[x.recv, ...x.args].map(expr).join(", ")})`;
    case "builtin": return `${x.name}(${x.args.map(expr).join(", ")})`;
    case "construct": return `${x.typeName}{${x.fields.map((f) => `${f.name}: ${expr(f.value)}`).join(", ")}}`;
    case "enumConstruct": return `${x.enumName}.${x.variant}{${x.fields.map((f) => `${f.name}: ${expr(f.value)}`).join(", ")}}`;
    // Both render the container from its full type (`Dict<Point, string>`), not from
    // `elem` alone — which would drop a dict's value type.
    case "collectionNew": return `${ty(x.type)}()`;
    case "collectionLit": {
      // A list/set prints its elements, a dict its `key: value` pairs, in the
      // surface literal's own brackets — so a Core dump reads like its source.
      const parts = x.entries.map((en) => (en.key ? `${expr(en.key)}: ${expr(en.value)}` : expr(en.value)));
      const [open, close] = x.container === "List" ? ["[", "]"] : ["{", "}"];
      return `${ty(x.type)}${open}${parts.join(", ")}${close}`;
    }
    case "builtinMethod": return `${expr(x.recv)}.${x.method}(${x.args.map(expr).join(", ")})`;
    // A first-class function value: a reference to a named function, or a closure
    // (its captured locals shown in `[…]`), or a call through such a value.
    case "funcRef": return `&${x.target}`;
    case "closure": {
      const ps = x.params.map((p) => `${p.name}: ${ty(p.type)}`).join(", ");
      const caps = x.captures.length ? ` [${x.captures.map((c) => c.name).join(", ")}]` : "";
      return `fn(${ps})${caps} { ${x.body.flatMap((s) => stmt(s, 0)).join("; ").trim()} }`;
    }
    case "callValue": return `${expr(x.callee)}(${x.args.map(expr).join(", ")})`;
  }
}

/** A compact type rendering (no checker dependency, so Core stays a leaf). */
function ty(t: Type): string {
  switch (t.k) {
    case "int": case "float": case "bool": case "string": case "char": case "void": case "unknown":
      return t.k;
    case "struct": case "enum": case "var":
      return t.name;
    case "struct-generic": case "enum-generic": case "generic": case "trait":
      return t.args.length ? `${t.name}<${t.args.map(ty).join(", ")}>` : t.name;
    case "fn": return `(${t.params.map(ty).join(", ")}) -> ${ty(t.ret)}`;
  }
}
